import api from './api';

/**
 * Bridge to the GIS-EYE 3D globe, mounted as its own portal app at geostack.com/globe/
 * (docs/GLOBE_INTEGRATION.md).
 *
 * Handoff: POST /api/integrations/globe/session returns a 15-minute HS256 JWT. On
 * geostack.com both apps share one origin, so we set GIS-EYE's `gev_session` cookie
 * (Path=/globe) ourselves before opening the globe. GIS-EYE has no `?token=` launch
 * parameter, and the token never travels in a URL. A visitor who reaches /globe/ without a session
 * (the portal card, a bookmark) is bounced by GIS-EYE to Ultimate-GIS's /globe-launch page
 * (GEV_LOGIN_URL), which does the same mint-and-cookie step and returns them. Anywhere else (localhost, pages.dev)
 * a cookie cannot reach the globe's host, so the globe's own portal sign-in takes over.
 */

export const GLOBE_COOKIE = 'gev_session';
export const GLOBE_FEATURE = 'globe';

/** `https://geostack.com/globe/?dataset=<signed url>&theme=<theme>` (dataset optional). */
export function buildGlobeUrl(base, { dataset, theme } = {}) {
  const url = new URL(base);
  if (dataset) url.searchParams.set('dataset', dataset);
  if (theme) url.searchParams.set('theme', theme === 'light' ? 'light' : 'dark');
  return url.toString();
}

/** The cookie string for the globe session; null when it cannot apply from this page. */
export function globeCookie(token, globeUrl, expiresAt, loc = window.location, now = Date.now()) {
  if (!token) return null;
  const target = new URL(globeUrl);
  if (loc.hostname !== target.hostname) return null; // cross-origin: JS cannot set it
  const path = target.pathname.replace(/\/+$/, '') || '/';
  const ms = expiresAt ? new Date(expiresAt).getTime() - now : 15 * 60 * 1000;
  const maxAge = Math.max(0, Math.floor(ms / 1000));
  const secure = loc.protocol === 'https:' ? '; Secure' : '';
  return `${GLOBE_COOKIE}=${encodeURIComponent(token)}; Path=${path}; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

/**
 * Mint a globe session and plant its cookie (when this page can). Resolves to the API body
 * `{token, expires_at, url, cookieSet}`; `url` is the globe launch URL.
 */
export async function startGlobeSession({ organizationId, theme } = {}, win = window) {
  const session = (await api.post('/api/integrations/globe/session', {
    organization_id: organizationId || undefined,
    theme,
  })).data;
  const cookie = globeCookie(session.token, session.url, session.expires_at, win.location);
  if (cookie) win.document.cookie = cookie;
  return { ...session, cookieSet: Boolean(cookie) };
}

/**
 * The URL to send somebody back to after /globe-launch, or null. GIS-EYE bounces a visitor
 * without a session to `<GEV_LOGIN_URL>?return=<the globe URL they asked for>`. Only a URL
 * under the globe's own origin and path qualifies (no open redirect), and the portal's
 * launch tokens are dropped from it so they do not stay in the address bar.
 */
export function safeGlobeReturn(returnUrl, globeUrl) {
  let target;
  let globe;
  try {
    target = new URL(returnUrl);
    globe = new URL(globeUrl);
  } catch {
    return null;
  }
  const base = globe.pathname.replace(/\/+$/, '');
  if (target.origin !== globe.origin) return null;
  if (target.pathname !== base && !target.pathname.startsWith(`${base}/`)) return null;
  ['access_token', 'refresh_token', 'token'].forEach((p) => target.searchParams.delete(p));
  return target.toString();
}

/**
 * Mint a session (and a dataset link when `datasetId` is given) and open the globe in a new
 * tab. The tab is opened synchronously so popup blockers allow it, then pointed at the URL.
 * Resolves to the opened URL; rejects with the API error (503 = not configured here).
 */
export async function openInGlobe({ organizationId, datasetId, theme } = {}, win = window) {
  const tab = win.open('', '_blank');
  try {
    const session = await startGlobeSession({ organizationId, theme }, win);
    let dataset;
    if (datasetId) {
      dataset = (await api.post('/api/integrations/globe/dataset-link', { dataset_id: datasetId })).data.url;
    }
    const target = buildGlobeUrl(session.url, { dataset, theme });
    if (tab) {
      tab.opener = null;
      tab.location.href = target;
    } else {
      win.location.assign(target);
    }
    return target;
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}
